import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { ToastContainer, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import './assets/styles/adminlte.css';

import LandingPage from './pages/LandingPage';
import NominationForm from './pages/NominationForm';
import NominationConfirm from './pages/NominationConfirm';
import NdaReview from './pages/NdaReview';
import Login from './components/Login';
import Register from './components/Register';
import RegisterOrganization from './pages/RegisterOrganization';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import CandidateSetup from './pages/CandidateSetup';
import Dashboard from './components/Dashboard';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import UserManagement from './pages/SuperAdmin/UserManagement';
import Organizations from './pages/SuperAdmin/Organizations';
import EditOrganization from './pages/SuperAdmin/EditOrganization';
import AuditLogs from './pages/SuperAdmin/AuditLogs';
import SuperAdminTransactions from './pages/SuperAdmin/Transactions';
import Payouts from './pages/SuperAdmin/Payouts';
import PricingPackages from './pages/SuperAdmin/PricingPackages';
import Subscriptions from './pages/SuperAdmin/Subscriptions';
import AdminDashboard from './pages/Admin/AdminDashboard';
import Elections from './pages/Admin/Elections';
import ElectionDetail from './pages/Admin/ElectionDetail';
import PositionCandidates from './pages/Admin/PositionCandidates';
import Transactions from './pages/Admin/Transactions';
import Results from './pages/Admin/Results';
import Reports from './pages/Admin/Reports';
import Settings from './pages/Admin/Settings';
import Members from './pages/Admin/Members';
import Groups from './pages/Admin/Groups';
import AdminPayouts from './pages/Admin/Payouts';
import PublicElection from './pages/PublicElection';
import TermsAndConditions from './pages/TermsAndConditions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import VoterOrganizations from './pages/Voter/Organizations';
import OrganizationElections from './pages/Voter/OrganizationElections';
import VotingPage from './pages/Voter/VotingPage';
import Wallet from './pages/Voter/Wallet';
import VoteWithToken from './pages/VoteWithToken';
import VoteWithId from './pages/VoteWithId';
import SpectatorView from './pages/SpectatorView';
import PaymentCallback from './pages/PaymentCallback';
import WalletCallback from './pages/WalletCallback';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';

function App() {
  return (
    <UserProvider>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/nominations/confirm" element={<NominationConfirm />} />
          <Route path="/nominations/:electionId" element={<NominationForm />} />
          <Route path="/nda/review" element={<NdaReview />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-organization" element={<RegisterOrganization />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/candidate-setup/:token" element={<CandidateSetup />} />
          
          {/* Public Election View */}
          <Route path="/elections/:id" element={<PublicElection />} />
          
          {/* Spectator View (No Auth Required) */}
          <Route path="/elections/:id/spectator" element={<SpectatorView />} />
          
          {/* Magic Link Voting (No Auth Required) */}
          <Route path="/vote/:token" element={<VoteWithToken />} />
          <Route path="/vote/id/:electionId" element={<VoteWithId />} />
          <Route path="/elections/:electionId/vote" element={<VotingPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          
          {/* Payment Callback */}
          <Route path="/payment/callback" element={<PaymentCallback />} />
          
          {/* Wallet Callback */}
          <Route path="/wallet/callback" element={<WalletCallback />} />

          {/* Voter Dashboard */}
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/voter/organizations"
            element={
              <PrivateRoute>
                <Layout>
                  <VoterOrganizations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/voter/organizations/:orgId/elections"
            element={
              <PrivateRoute>
                <Layout>
                  <OrganizationElections />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/voter/elections/:electionId/vote"
            element={
              <PrivateRoute>
                <Layout>
                  <VotingPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/wallet"
            element={
              <PrivateRoute>
                <Layout>
                  <Wallet />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/super-admin/dashboard"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <SuperAdminDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/users"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <UserManagement />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/organizations"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <Organizations />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/organizations/:id"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <EditOrganization />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/audit-logs"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <AuditLogs />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/transactions"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <SuperAdminTransactions />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/payouts"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <Payouts />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/pricing-packages"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <PricingPackages />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/subscriptions"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <Subscriptions />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/dashboard"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <AdminDashboard />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/payouts"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <AdminPayouts />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/elections"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Elections />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/elections/:id"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <ElectionDetail />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/positions/:positionId/candidates"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <PositionCandidates />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/transactions"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Transactions />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/results"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Results />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/reports"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Reports />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/members"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Members />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/groups"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Groups />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <PrivateRoute requiredRole="admin">
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/settings"
            element={
              <PrivateRoute requiredRole="super_admin">
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            }
          />

          {/* Add more routes here as you build them */}
        </Routes>
        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          transition={Slide}
        />
      </Router>
    </UserProvider>
  );
}

export default App;
